import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import {fetchBaseQuery} from '@reduxjs/toolkit/query/react';

import {edumeClientFetchArgs} from '../../edumeClient';
import {mstClientFetchArgs} from '../../edumeMstClient';
import {edumeReportsClientFetchArgs} from '../../edumeReportsClient';
import {MS_TEAMS} from '../../enums/integrations';
import {guidesClientFetchArgs} from '../../guidesClient';
import * as authActions from '../auth/authActions';

export type ClientFetchArgs = Pick<
  FetchBaseQueryArgs,
  'baseUrl' | 'prepareHeaders'
>;

type ApiClientType = 'default' | 'mst' | 'guides' | 'reports';

const getClientArgs = (client?: ApiClientType): ClientFetchArgs => {
  switch (client) {
    case 'mst':
      return mstClientFetchArgs;

    case 'guides':
      return guidesClientFetchArgs;

    case 'reports':
      return edumeReportsClientFetchArgs;

    case 'default':
    default:
      if (sessionStorage.getItem('accessMethod') === MS_TEAMS) {
        return mstClientFetchArgs;
      }

      return edumeClientFetchArgs;
  }
};

function isErrorWithMessage<T extends FetchBaseQueryError>(
  error: T
): error is T & {
  data: T['data'] & {
    message?: unknown;
    errorCode?: unknown;
  };
} {
  return (
    typeof error.data === 'object' &&
    !!error.data &&
    ('message' in error.data || 'errorCode' in error.data)
  );
}

function handleQueryAuthError(error: FetchBaseQueryError, api: BaseQueryApi) {
  if (!isErrorWithMessage(error)) {
    return;
  }

  const {data} = error;

  if (error?.status === 401) {
    if (data.message === 'Expired token') {
      api.dispatch(authActions.handleExpiredToken());
      return;
    }

    if (data.message === 'Old token version') {
      api.dispatch(authActions.handleOldTokenVersion());
      return;
    }

    if (
      data.message === 'Missing authentication' ||
      data.message === 'Invalid token'
    ) {
      api.dispatch(authActions.handleInvalidToken());
      return;
    }
  } else if (data.errorCode === 'deleted_user') {
    api.dispatch(authActions.handleDeletedUser());
    return;
  }
}

export const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  {
    client?: ApiClientType;
  }
> = async (args, api, extraOptions) => {
  const clientArgs = getClientArgs(extraOptions?.client);
  const customFetchBaseQuery = fetchBaseQuery(clientArgs);
  const result = await customFetchBaseQuery(args, api, extraOptions);

  if (result.error) {
    handleQueryAuthError(result.error, api);
  }

  return result;
};
